<template>
  <div v-if="hasAccess" class="meetings-page">
    <page-content class="meetings-page__content">
      <section class="meetings-panel">
        <div v-if="loading && !meeting" class="meetings-state">
          <i class="fas fa-spinner fa-spin" aria-hidden="true"></i> Chargement de la réunion…
        </div>
        <div v-else-if="loadError && !meeting" class="meetings-state meetings-state--error">
          <span>{{ loadError }}</span>
          <button v-if="loadErrorRetryable" type="button" @click="load()">Réessayer</button>
          <router-link :to="{ name: 'app-meetings' }">Retour aux réunions</router-link>
        </div>

        <template v-else-if="meeting">
          <header class="meetings-header">
            <div class="meetings-header__main">
              <router-link :to="{ name: 'app-meetings' }" class="meetings-back">
                <i class="fas fa-arrow-left" aria-hidden="true"></i> Réunions CSE
              </router-link>
              <h2 class="meetings-header__title">{{ meeting.title || 'Réunion sans titre' }}</h2>
              <p class="meetings-header__subtitle meeting-meta">
                <span v-if="meetingDateLabel">{{ meetingDateLabel }}</span>
                <span>{{ inputLabel }}</span>
                <span v-if="audioFile && audioFile.name" class="meeting-meta__file" :title="audioFile.name">
                  {{ audioFile.name }}<template v-if="audioFile.size"> ({{ formatFileSize(audioFile.size) }})</template>
                </span>
                <span v-if="purgedAtLabel" title="L’original et la copie d’écoute sont effacés quelques jours après la rédaction des livrables : seul le mot à mot est conservé.">
                  Enregistrement effacé le {{ purgedAtLabel }}
                </span>
                <span v-else-if="purgeAtLabel" title="L’écoute reste possible dans le mot à mot et la correction du locuteur jusqu’à cette date, puis l’enregistrement est effacé.">
                  Enregistrement conservé jusqu’au {{ purgeAtLabel }}
                </span>
                <span v-if="durationLabel">{{ durationLabel }}</span>
                <span v-if="wordCountLabel">{{ wordCountLabel }}</span>
                <span v-if="speakerCountLabel">{{ speakerCountLabel }}</span>
                <span v-if="createdAtLabel">Déposée le {{ createdAtLabel }}</span>
              </p>
            </div>
            <div class="meetings-header__actions">
              <button
                v-if="meeting.status === 'failed'"
                type="button"
                class="meetings-outline meetings-outline--primary"
                :disabled="retrying"
                @click="retry"
              >
                <i :class="retrying ? 'fas fa-spinner fa-spin' : 'fas fa-redo'" aria-hidden="true"></i>
                Relancer
              </button>
              <button
                type="button"
                class="meetings-outline meetings-outline--danger"
                :disabled="deleting"
                @click="confirmDelete"
              >
                <i :class="deleting ? 'fas fa-spinner fa-spin' : 'far fa-trash-alt'" aria-hidden="true"></i>
                Supprimer
              </button>
            </div>
          </header>

          <div class="meeting-body">
            <div class="meeting-banner" :class="`meeting-banner--${meeting.status}`" aria-live="polite">
              <div class="meeting-banner__main">
                <meeting-status-pill :status="meeting.status" />
                <p>{{ bannerText }}</p>
              </div>
              <ol v-if="processing || reviewing" class="meeting-steps" aria-label="Avancement du traitement">
                <li
                  v-for="step in steps"
                  :key="step.key"
                  class="meeting-step"
                  :class="`meeting-step--${step.state}`"
                >
                  <span class="meeting-step__marker" aria-hidden="true">
                    <i v-if="step.state === 'done'" class="fas fa-check"></i>
                    <i v-else-if="step.state === 'current'" class="fas fa-circle-notch fa-spin"></i>
                  </span>
                  <span>{{ step.label }}</span>
                </li>
              </ol>
              <p v-if="meeting.status === 'failed' && meeting.error" class="meeting-banner__error">
                {{ meeting.error }}
              </p>
            </div>

            <div v-if="warnings.length" class="meeting-warnings">
              <p class="meeting-warnings__title">
                <i class="fas fa-exclamation-triangle" aria-hidden="true"></i>
                Points d’attention signalés par l’assistant
              </p>
              <ul>
                <li v-for="(warning, index) in warnings" :key="index">{{ warning }}</li>
              </ul>
            </div>

            <nav class="meetings-tabs meeting-tabs" aria-label="Livrables de la réunion">
              <button
                v-for="tab in tabs"
                :key="tab.key"
                type="button"
                class="meetings-tabs__item"
                :class="{ 'meetings-tabs__item--active': currentTab === tab.key }"
                @click="activeTab = tab.key"
              >
                {{ tab.label }}
                <span v-if="tab.count !== null" class="meetings-tabs__count">{{ tab.count }}</span>
              </button>
            </nav>

            <section class="meeting-tab-body">
              <div v-if="currentTab === 'summary'">
                <template v-if="meeting.summary">
                  <div class="meeting-section-actions">
                    <button type="button" class="meetings-outline meetings-outline--small" @click="copy('summary', meeting.summary)">
                      <i :class="copied === 'summary' ? 'fas fa-check' : 'far fa-copy'" aria-hidden="true"></i>
                      {{ copied === 'summary' ? 'Copié !' : 'Copier le résumé' }}
                    </button>
                  </div>
                  <div class="md-content" v-html="summaryHtml"></div><!-- eslint-disable-line vue/no-v-html -->
                </template>
                <p v-else class="meeting-placeholder">{{ placeholderText('Le résumé') }}</p>
              </div>

              <div v-else-if="currentTab === 'review'">
                <meeting-speakers
                  v-if="speakerParagraphs.length"
                  :meeting="meeting"
                  :readonly="processing"
                  :saving="speakersSaving"
                  @save="saveSpeakerNames"
                />
                <meeting-review
                  :meeting="meeting"
                  :readonly="!reviewing"
                  :submitting="reviewSubmitting"
                  :moving-speaker="speakerMoving"
                  @submit="submitReview"
                  @move-speaker="moveSpeaker"
                />
              </div>

              <div v-else-if="currentTab === 'transcript'">
                <template v-if="speakerParagraphs.length">
                  <meeting-speakers
                    :meeting="meeting"
                    :readonly="processing"
                    :saving="speakersSaving"
                    @save="saveSpeakerNames"
                  />
                  <div class="meeting-section-actions">
                    <button type="button" class="meetings-outline meetings-outline--small" @click="copy('transcript', transcriptText)">
                      <i :class="copied === 'transcript' ? 'fas fa-check' : 'far fa-copy'" aria-hidden="true"></i>
                      {{ copied === 'transcript' ? 'Copié !' : 'Copier le mot à mot' }}
                    </button>
                  </div>
                  <article
                    v-for="(paragraph, index) in speakerParagraphs"
                    :key="index"
                    class="meeting-speech"
                    :class="{ 'meeting-speech--fixing': fixingParagraph === index, 'meeting-speech--mixed': paragraph.mixed }"
                  >
                    <header class="meeting-speech__header">
                      <span class="meeting-speech__speaker" :class="paragraph.mixed ? 'meeting-speech__speaker--mixed' : `meeting-speech__speaker--${(paragraph.index - 1) % 6}`">
                        {{ paragraph.speaker }}
                      </span>
                      <span v-if="paragraph.start !== null && paragraph.start !== undefined" class="meeting-speech__time">
                        {{ formatTimecode(paragraph.start) }}
                      </span>
                      <button
                        v-if="canFixSpeakers && fixingParagraph !== index"
                        type="button"
                        class="meeting-speech__fix"
                        :class="{ 'meeting-speech__fix--doubt': doubtfulCounts[index] > 0 }"
                        :title="doubtfulCounts[index] > 0
                          ? 'Cette prise de parole contient des répliques courtes, souvent d’une autre personne : vérifiez-les.'
                          : 'Une phrase de cette prise de parole est d’une autre personne ? Réattribuez-la.'"
                        @click="fixingParagraph = index"
                      >
                        <i class="fas fa-user-edit" aria-hidden="true"></i> Corriger le locuteur
                        <i v-if="doubtfulCounts[index] > 0" class="fas fa-exclamation-triangle meeting-speech__doubt" aria-hidden="true"></i>
                      </button>
                    </header>
                    <meeting-speaker-fix
                      v-if="fixingParagraph === index"
                      :meeting="meeting"
                      :first="paragraph.firstIndex"
                      :last="paragraph.lastIndex"
                      :saving="speakerMoving"
                      @move="moveSpeaker"
                      @cancel="fixingParagraph = null"
                    />
                    <p v-else class="meeting-speech__text">{{ paragraph.text }}</p>
                  </article>
                </template>
                <template v-else-if="meeting.transcript">
                  <div class="meeting-section-actions">
                    <span class="meeting-section-actions__hint">Locuteurs non identifiés par le fournisseur de transcription.</span>
                    <button type="button" class="meetings-outline meetings-outline--small" @click="copy('transcript', meeting.transcript)">
                      <i :class="copied === 'transcript' ? 'fas fa-check' : 'far fa-copy'" aria-hidden="true"></i>
                      {{ copied === 'transcript' ? 'Copié !' : 'Copier le mot à mot' }}
                    </button>
                  </div>
                  <p class="meeting-transcript">{{ meeting.transcript }}</p>
                </template>
                <p v-else class="meeting-placeholder">{{ placeholderText('Le mot à mot') }}</p>
              </div>

              <div v-else-if="currentTab === 'news'">
                <template v-if="newsProposals.length">
                  <p class="meeting-section-intro">
                    Des idées d’actualités à publier dans l’application, avec un brief pour en rédiger le corps.
                    Rien n’est publié automatiquement.
                  </p>
                  <article
                    v-for="(proposal, index) in newsProposals"
                    :key="index"
                    class="meeting-proposal"
                  >
                    <div class="meeting-proposal__body">
                      <h3 class="meeting-proposal__title">{{ proposal.title }}</h3>
                      <p v-if="proposal.brief" class="meeting-proposal__brief">{{ proposal.brief }}</p>
                    </div>
                    <button type="button" class="meetings-outline meetings-outline--small" @click="copy(`news-${index}`, proposalText(proposal))">
                      <i :class="copied === `news-${index}` ? 'fas fa-check' : 'far fa-copy'" aria-hidden="true"></i>
                      {{ copied === `news-${index}` ? 'Copié !' : 'Copier' }}
                    </button>
                  </article>
                </template>
                <p v-else class="meeting-placeholder">
                  {{ meeting.status === 'completed' ? 'Aucune proposition d’actualité n’a été retenue pour cette réunion.' : placeholderText('Les propositions d’actualités') }}
                </p>
              </div>

              <div v-else-if="currentTab === 'follow_ups'">
                <p v-if="memoryUsed === 'dossier' && followUps.length" class="meeting-section-intro">
                  Rédigé à partir du <router-link :to="{ name: 'app-meetings-context' }">dossier de suivi du CSE</router-link>,
                  mis à jour par cette réunion. Les points qui en sont repris sont signalés.
                </p>
                <p v-else-if="previousMeeting && followUps.length" class="meeting-section-intro">
                  Premier <router-link :to="{ name: 'app-meetings-context' }">dossier de suivi</router-link> du CSE, créé à partir de la réunion précédente :
                  <router-link :to="{ name: 'app-meeting', params: { uuid: previousMeeting.uuid } }">{{ previousMeeting.title }}</router-link><template v-if="previousMeetingDate"> du {{ previousMeetingDate }}</template>.
                  Les points qui en sont repris sont signalés.
                </p>
                <p v-else-if="contextVersion && followUps.length" class="meeting-section-intro">
                  Cette réunion a créé le <router-link :to="{ name: 'app-meetings-context' }">dossier de suivi</router-link> du CSE.
                </p>
                <meeting-follow-ups
                  v-if="followUps.length || meeting.status === 'completed'"
                  :meeting="meeting"
                  :readonly="processing"
                  @change="replaceMeeting"
                  @conflict="onFollowUpsConflict"
                  @notify="notify"
                />
                <p v-else class="meeting-placeholder">
                  {{ placeholderText('Les points de suivi') }}
                </p>
              </div>
            </section>
          </div>

          <details v-if="log.length" class="meeting-log">
            <summary>Journal de traitement ({{ log.length }})</summary>
            <ul>
              <li v-for="(entry, index) in log" :key="index" class="meeting-log__entry" :class="`meeting-log__entry--${entry.level}`">
                <time>{{ formatLogTime(entry.at) }}</time>
                <span>{{ entry.message }}</span>
              </li>
            </ul>
          </details>
        </template>
      </section>
    </page-content>
  </div>

  <div v-else class="meetings-no-access">
    <p>Vous n’avez pas accès à cette section.</p>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { marked } from 'marked'
import PageContent from '@/components/PageContent.vue'
import aiService from '@/services/ai.service'
import MeetingStatusPill from './MeetingStatusPill.vue'
import MeetingReview from './MeetingReview.vue'
import MeetingSpeakers from './MeetingSpeakers.vue'
import MeetingSpeakerFix from './MeetingSpeakerFix.vue'
import MeetingFollowUps from './MeetingFollowUps.vue'
import {
  MEETING_ROLES,
  POLL_INTERVAL_MS,
  isProcessing,
  inputTypeLabel,
  formatMeetingDate,
  formatDateTime,
  formatLogTime,
  formatFileSize,
  formatDuration,
  formatTimecode,
  formatNumber,
  speakerParagraphs,
  doubtfulPositions,
  copyToClipboard,
  escapeHtml
} from './meetingHelpers'

const STEP_ORDER = ['queued', 'transcribing', 'reviewing', 'synthesizing', 'completed']

const STEP_LABELS = {
  queued: 'Dépôt',
  transcribing: 'Transcription',
  reviewing: 'Relecture',
  synthesizing: 'Rédaction',
  completed: 'Livrables'
}

/** Étapes propres à un enregistrement audio : absentes pour des notes. */
const AUDIO_ONLY_STEPS = ['transcribing', 'reviewing']

/**
 * Fiche d'une réunion CSE : état du traitement (rafraîchi toutes les 5 s tant
 * qu'il n'est pas terminé), relecture des corrections proposées sur le mot à
 * mot quand la réunion l'attend, puis les livrables en onglets. Le mot à mot
 * n'existe que pour un enregistrement audio.
 */
export default {
  name: 'app-meeting-one',
  components: { PageContent, MeetingStatusPill, MeetingReview, MeetingSpeakers, MeetingSpeakerFix, MeetingFollowUps },
  data () {
    return {
      meeting: null,
      loading: true,
      loadError: '',
      loadErrorRetryable: true,
      activeTab: 'summary',
      copied: null,
      copiedTimer: null,
      retrying: false,
      deleting: false,
      reviewSubmitting: false,
      speakersSaving: false,
      speakerMoving: false,
      /** Prise de parole du mot à mot dépliée pour corriger un locuteur (index dans speakerParagraphs). */
      fixingParagraph: null,
      pollTimer: null
    }
  },
  computed: {
    ...mapGetters(['roles', 'domain']),
    hasAccess () {
      return this.roles.some(role => MEETING_ROLES.includes(role))
    },
    uuid () {
      return this.$route.params.uuid
    },
    processing () {
      return !!this.meeting && isProcessing(this.meeting.status)
    },
    reviewing () {
      return !!this.meeting && this.meeting.status === 'reviewing'
    },
    /** Une phrase peut être réattribuée dès que la machine ne travaille plus sur la réunion. */
    canFixSpeakers () {
      return !!this.meeting && !this.processing
    },
    /** Avancement des tranches d'un enregistrement long, pendant la transcription. */
    chunkProgress () {
      const chunks = this.transcription.chunks
      return chunks && Number(chunks.total) > 1 ? { done: Number(chunks.done) || 0, total: Number(chunks.total) } : null
    },
    proposals () {
      return Array.isArray(this.meeting.transcript_proposals)
        ? this.meeting.transcript_proposals.filter(proposal => proposal && proposal.from)
        : []
    },
    isAudio () {
      return !!this.meeting && this.meeting.input_type === 'audio'
    },
    inputLabel () {
      return inputTypeLabel(this.meeting.input_type)
    },
    audioFile () {
      return this.meeting.audio_file && typeof this.meeting.audio_file === 'object' ? this.meeting.audio_file : null
    },
    purgedAtLabel () {
      return this.audioFile && this.audioFile.purged_at ? formatDateTime(this.audioFile.purged_at) : ''
    },
    /** Date prévue de l'effacement de l'enregistrement, une fois les livrables rédigés. */
    purgeAtLabel () {
      return this.meeting.recording_purge_at ? formatDateTime(this.meeting.recording_purge_at) : ''
    },
    transcription () {
      return this.meeting.transcription && typeof this.meeting.transcription === 'object' ? this.meeting.transcription : {}
    },
    meetingDateLabel () {
      return formatMeetingDate(this.meeting.meeting_date)
    },
    createdAtLabel () {
      return formatDateTime(this.meeting.created_at)
    },
    completedAtLabel () {
      return formatDateTime(this.meeting.completed_at)
    },
    durationLabel () {
      return formatDuration(this.transcription.duration_seconds)
    },
    wordCountLabel () {
      const count = Number(this.transcription.word_count)
      return count > 0 ? `${formatNumber(count)} mots` : ''
    },
    speakerCountLabel () {
      const count = Number(this.transcription.speaker_count)
      if (!count) {
        return ''
      }
      return count > 1 ? `${count} locuteurs` : '1 locuteur'
    },
    warnings () {
      return Array.isArray(this.meeting.warnings) ? this.meeting.warnings.filter(Boolean) : []
    },
    newsProposals () {
      return Array.isArray(this.meeting.news_proposals)
        ? this.meeting.news_proposals.filter(proposal => proposal && proposal.title)
        : []
    },
    followUps () {
      return Array.isArray(this.meeting.follow_ups)
        ? this.meeting.follow_ups.filter(followUp => followUp && followUp.item)
        : []
    },
    /** Réunion dont les livrables ont été transmis à la rédaction, d'après les métadonnées de synthèse. */
    previousMeeting () {
      const synthesis = this.meeting.synthesis && typeof this.meeting.synthesis === 'object' ? this.meeting.synthesis : {}
      const previous = synthesis.previous_meeting
      return previous && previous.uuid ? previous : null
    },
    previousMeetingDate () {
      return this.previousMeeting ? formatMeetingDate(this.previousMeeting.date) : ''
    },
    /** Ce qui a servi de mémoire à la rédaction : le dossier de suivi, la réunion précédente ou rien. */
    memoryUsed () {
      const synthesis = this.meeting.synthesis && typeof this.meeting.synthesis === 'object' ? this.meeting.synthesis : {}
      return synthesis.context_used && synthesis.context_used.from ? synthesis.context_used.from : null
    },
    contextVersion () {
      const synthesis = this.meeting.synthesis && typeof this.meeting.synthesis === 'object' ? this.meeting.synthesis : {}
      return synthesis.context_version || null
    },
    log () {
      return Array.isArray(this.meeting.log) ? this.meeting.log.filter(entry => entry && entry.message) : []
    },
    /** Noms donnés aux voix par le relecteur, par étiquette brute du fournisseur. */
    speakerNames () {
      return this.meeting.speaker_names && typeof this.meeting.speaker_names === 'object' ? this.meeting.speaker_names : {}
    },
    speakerParagraphs () {
      return this.isAudio ? speakerParagraphs(this.meeting.transcript_segments, this.speakerNames) : []
    },
    /** Par prise de parole, le nombre de phrases dont le locuteur mérite une vérification. */
    doubtfulCounts () {
      const segments = this.meeting.transcript_segments
      return this.speakerParagraphs.map(paragraph => doubtfulPositions(segments, paragraph.firstIndex, paragraph.lastIndex).length)
    },
    transcriptText () {
      return this.speakerParagraphs
        .map(paragraph => `${paragraph.speaker} : ${paragraph.text}`)
        .join('\n\n')
    },
    summaryHtml () {
      return this.meeting.summary ? marked.parse(String(this.meeting.summary), { breaks: true }) : ''
    },
    tabs () {
      const tabs = []
      if (this.isAudio && (this.reviewing || this.proposals.length)) {
        // La relecture vient en premier : c'est l'étape à faire avant les livrables.
        tabs.push({ key: 'review', label: 'Relecture', count: this.proposals.length })
      }
      tabs.push({ key: 'summary', label: 'Résumé', count: null })
      if (this.isAudio) {
        tabs.push({ key: 'transcript', label: 'Mot à mot', count: null })
      }
      tabs.push({ key: 'news', label: 'Propositions d’actus', count: this.newsProposals.length })
      tabs.push({ key: 'follow_ups', label: 'Points de suivi', count: this.followUps.length })
      return tabs
    },
    currentTab () {
      return this.tabs.some(tab => tab.key === this.activeTab) ? this.activeTab : 'summary'
    },
    steps () {
      const statusIndex = STEP_ORDER.indexOf(this.meeting.status)
      return STEP_ORDER
        .filter(key => !AUDIO_ONLY_STEPS.includes(key) || this.isAudio)
        .map(key => {
          const index = STEP_ORDER.indexOf(key)
          let state = 'todo'
          if (index < statusIndex) {
            state = 'done'
          } else if (index === statusIndex) {
            state = 'current'
          }
          return { key, label: STEP_LABELS[key], state }
        })
    },
    bannerText () {
      switch (this.meeting.status) {
        case 'queued':
          return 'La réunion est en file d’attente : le traitement démarre dès qu’un créneau se libère. Cette page se met à jour toute seule.'
        case 'transcribing':
          if (this.chunkProgress) {
            return `Enregistrement long : transcription par tranches, ${this.chunkProgress.done} sur ${this.chunkProgress.total} déjà faite${this.chunkProgress.done > 1 ? 's' : ''}. Comptez plusieurs minutes par tranche ; cette page se met à jour toute seule.`
          }
          return 'L’enregistrement est en cours de transcription. Comptez plusieurs minutes pour deux heures de réunion ; cette page se met à jour toute seule.'
        case 'reviewing':
          if (!this.proposals.length) {
            return 'Le mot à mot est prêt, mais la relecture automatique n’a rien pu proposer. Lancez la rédaction des livrables depuis l’onglet Relecture.'
          }
          return this.proposals.length > 1
            ? `Le mot à mot est prêt. L’assistant a relevé ${this.proposals.length} passages douteux : tranchez-les dans l’onglet Relecture, puis lancez la rédaction des livrables.`
            : 'Le mot à mot est prêt. L’assistant a relevé un passage douteux : tranchez-le dans l’onglet Relecture, puis lancez la rédaction des livrables.'
        case 'synthesizing':
          return this.isAudio
            ? 'Le mot à mot est prêt : l’assistant rédige maintenant le résumé, les propositions d’actualités et les points de suivi.'
            : 'L’assistant analyse vos notes et rédige le résumé, les propositions d’actualités et les points de suivi.'
        case 'completed':
          return this.completedAtLabel
            ? `Traitement terminé le ${this.completedAtLabel}.`
            : 'Traitement terminé.'
        case 'failed':
          return 'Le traitement a échoué. Vous pouvez le relancer ; si l’erreur persiste, contactez l’équipe Maviepro.'
        default:
          return ''
      }
    }
  },
  watch: {
    uuid () {
      this.meeting = null
      this.activeTab = 'summary'
      this.fixingParagraph = null
      this.load()
    },
    currentTab () {
      this.fixingParagraph = null
    },
    'domain.uuid' () {
      // La fiche appartient au domaine de l'en-tête : un changement de domaine
      // la rendrait introuvable (404), on revient à la liste du nouveau domaine.
      this.$router.replace({ name: 'app-meetings' }).catch(() => {})
    }
  },
  created () {
    this.load()
  },
  beforeDestroy () {
    this.stopPolling()
    if (this.copiedTimer) {
      window.clearTimeout(this.copiedTimer)
    }
  },
  methods: {
    formatFileSize,
    formatTimecode,
    formatLogTime,
    load (silent = false) {
      this.stopPolling()
      if (!this.uuid) {
        return
      }
      if (!silent) {
        this.loading = true
        this.loadError = ''
      }
      aiService.getMeeting(this.uuid).then(meeting => {
        this.meeting = meeting && meeting.uuid ? meeting : null
        this.loadError = this.meeting ? '' : 'Réunion introuvable.'
        if (this.reviewing && this.activeTab === 'summary') {
          // Une réunion qui attend l'élu s'ouvre sur ce qu'il a à faire.
          this.activeTab = 'review'
        }
        this.schedulePolling()
      }).catch(err => {
        const status = err && err.response ? err.response.status : null
        if (status === 404) {
          this.loadError = 'Réunion introuvable sur ce domaine : elle a peut-être été supprimée.'
          this.loadErrorRetryable = false
          this.meeting = null
        } else if (status === 403) {
          this.loadError = 'Vous n’avez pas le rôle requis pour consulter cette réunion.'
          this.loadErrorRetryable = false
          this.meeting = null
        } else {
          this.loadError = 'Impossible de charger la réunion.'
          this.loadErrorRetryable = true
          // Un rafraîchissement raté en cours de traitement ne vide pas la
          // fiche : on retente au prochain tour.
          if (silent) {
            this.schedulePolling()
          }
        }
      }).finally(() => {
        this.loading = false
      })
    },
    schedulePolling () {
      this.stopPolling()
      if (this.processing) {
        this.pollTimer = window.setTimeout(() => this.load(true), POLL_INTERVAL_MS)
      }
    },
    stopPolling () {
      if (this.pollTimer) {
        window.clearTimeout(this.pollTimer)
        this.pollTimer = null
      }
    },
    placeholderText (subject) {
      if (this.meeting.status === 'failed') {
        return `${subject} n’a pas pu être produit : relancez le traitement.`
      }
      if (this.reviewing) {
        return `${subject} sera rédigé une fois la relecture du mot à mot validée.`
      }
      return `${subject} sera disponible à la fin du traitement.`
    },
    submitReview (decisions) {
      if (this.reviewSubmitting) {
        return
      }
      this.reviewSubmitting = true
      aiService.reviewMeeting(this.uuid, decisions).then(payload => {
        const summary = payload && payload.review_summary ? payload.review_summary : {}
        if (payload && payload.uuid) {
          const meeting = Object.assign({}, payload)
          delete meeting.review_summary
          this.meeting = meeting
        }
        const applied = Number(summary.accepted || 0) + Number(summary.edited || 0)
        const parts = []
        if (applied) {
          parts.push(applied > 1 ? `${applied} corrections appliquées` : 'une correction appliquée')
        }
        if (summary.glossary_rules) {
          parts.push(summary.glossary_rules > 1 ? `${summary.glossary_rules} règles ajoutées au glossaire` : 'une règle ajoutée au glossaire')
        }
        this.notify('is-success', parts.length
          ? `Relecture enregistrée : ${parts.join(', ')}. Rédaction des livrables lancée.`
          : 'Rédaction des livrables lancée.')
        this.activeTab = 'summary'
        this.schedulePolling()
      }).catch(err => {
        const status = err && err.response ? err.response.status : null
        if (status === 409) {
          this.notify('is-warning', 'Cette réunion n’attend plus de relecture : la fiche a été rechargée.')
          this.load(true)
          return
        }
        if (status === 422) {
          this.notify('is-danger', 'Décisions incomplètes : vérifiez les corrections saisies.')
          return
        }
        this.notify('is-danger', status === 403
          ? 'Vous n’avez pas le rôle requis pour relire cette réunion.'
          : 'Impossible d’enregistrer la relecture.')
      }).finally(() => {
        this.reviewSubmitting = false
      })
    },
    /**
     * Noms des voix saisis dans le cadre de nommage : la réunion renvoyée
     * remplace la fiche, ce qui met à jour le mot à mot et la relecture.
     */
    saveSpeakerNames (names) {
      if (this.speakersSaving) {
        return
      }
      this.speakersSaving = true
      aiService.nameMeetingSpeakers(this.uuid, names).then(payload => {
        if (payload && payload.uuid) {
          this.meeting = payload
        }
      }).catch(err => {
        const status = err && err.response ? err.response.status : null
        if (status === 422) {
          this.notify('is-danger', 'Nom de locuteur trop long : 80 caractères au plus.')
          return
        }
        this.notify('is-danger', status === 403
          ? 'Vous n’avez pas le rôle requis pour nommer les locuteurs.'
          : 'Impossible d’enregistrer les noms des locuteurs.')
      }).finally(() => {
        this.speakersSaving = false
      })
    },
    /**
     * Phrase réattribuée à une autre voix depuis le mot à mot ou la relecture
     * (`range` : positions from/to des segments et étiquette brute cible) : la
     * réunion renvoyée remplace la fiche, les prises de parole se recomposent.
     */
    moveSpeaker (range) {
      if (this.speakerMoving) {
        return
      }
      this.speakerMoving = true
      aiService.moveMeetingSegments(this.uuid, range).then(meeting => {
        if (meeting && meeting.uuid) {
          this.meeting = meeting
        }
        this.fixingParagraph = null
        const name = escapeHtml(this.speakerNameAt(range.from))
        this.notify('is-success', range.to > range.from ? `Passage attribué à ${name}.` : `Phrase attribuée à ${name}.`)
      }).catch(err => {
        const status = err && err.response ? err.response.status : null
        if (status === 409) {
          this.notify('is-warning', 'Le mot à mot ne peut pas être modifié pendant le traitement : la fiche a été rechargée.')
          this.fixingParagraph = null
          this.load(true)
          return
        }
        if (status === 422) {
          this.notify('is-danger', 'Sélection invalide : rechargez la fiche et recommencez.')
          return
        }
        this.notify('is-danger', status === 403
          ? 'Vous n’avez pas le rôle requis pour corriger le mot à mot.'
          : 'Impossible de corriger le locuteur.')
      }).finally(() => {
        this.speakerMoving = false
      })
    },
    /** Fiche renvoyée par une action du tableau des points de suivi. */
    replaceMeeting (meeting) {
      if (meeting && meeting.uuid) {
        this.meeting = meeting
      }
    },
    /** Le point n'existe plus ou la réunion est repassée en traitement : la fiche est rechargée. */
    onFollowUpsConflict () {
      this.notify('is-warning', 'La fiche a été rechargée.')
      this.load(true)
    },
    /** Nom affiché de la voix qui porte le segment à cette position, d'après la fiche courante. */
    speakerNameAt (position) {
      const paragraph = this.speakerParagraphs.find(candidate => candidate.firstIndex <= position && position <= candidate.lastIndex)
      return paragraph ? paragraph.speaker : 'la voix choisie'
    },
    proposalText (proposal) {
      return [proposal.title, proposal.brief].filter(Boolean).join('\n\n')
    },
    copy (key, text) {
      copyToClipboard(text).then(() => {
        this.copied = key
        if (this.copiedTimer) {
          window.clearTimeout(this.copiedTimer)
        }
        this.copiedTimer = window.setTimeout(() => {
          if (this.copied === key) {
            this.copied = null
          }
        }, 2000)
      }).catch(() => {
        this.notify('is-danger', 'Impossible de copier dans le presse-papiers.')
      })
    },
    retry () {
      if (this.retrying) {
        return
      }
      this.retrying = true
      aiService.retryMeeting(this.uuid).then(meeting => {
        if (meeting && meeting.uuid) {
          this.meeting = meeting
        }
        this.notify('is-success', 'Traitement relancé.')
        this.schedulePolling()
      }).catch(err => {
        const status = err && err.response ? err.response.status : null
        if (status === 409) {
          this.notify('is-warning', 'Cette réunion n’est pas en échec : le traitement est déjà en cours ou terminé.')
          this.load(true)
          return
        }
        this.notify('is-danger', status === 403
          ? 'Vous n’avez pas le rôle requis pour relancer ce traitement.'
          : 'Impossible de relancer le traitement.')
      }).finally(() => {
        this.retrying = false
      })
    },
    confirmDelete () {
      if (this.deleting) {
        return
      }
      const title = escapeHtml(this.meeting.title || 'cette réunion')
      this.$buefy.dialog.confirm({
        title: 'Supprimer la réunion',
        message: `Supprimer <b>${title}</b> ainsi que son mot à mot et ses livrables ? Cette action est définitive.`,
        type: 'is-danger',
        hasIcon: true,
        icon: 'exclamation-triangle',
        cancelText: 'Annuler',
        confirmText: 'Supprimer',
        onConfirm: () => this.deleteMeeting()
      })
    },
    deleteMeeting () {
      this.deleting = true
      const title = escapeHtml(this.meeting.title || 'La réunion')
      aiService.deleteMeeting(this.uuid).then(() => {
        this.stopPolling()
        this.notify('is-success', `<strong>${title}</strong> a été supprimée.`)
        this.$router.replace({ name: 'app-meetings' }).catch(() => {})
      }).catch(err => {
        const status = err && err.response ? err.response.status : null
        this.notify('is-danger', status === 403
          ? 'Vous n’avez pas le rôle requis pour supprimer une réunion.'
          : 'Impossible de supprimer cette réunion.')
      }).finally(() => {
        this.deleting = false
      })
    },
    notify (type, message) {
      this.$buefy.notification.open({
        duration: 4000,
        message,
        position: 'is-bottom-right',
        type,
        hasIcon: false
      })
    }
  }
}
</script>

<style src="./meetings.css"></style>

<style scoped>
.meeting-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.2rem 0;
}

.meeting-meta > span + span::before {
  margin: 0 0.45rem;
  color: #9ca3af;
  content: '·';
}

.meeting-meta__file {
  max-width: 100%;
  overflow-wrap: anywhere;
}

.meeting-body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.25rem 1.5rem 1.5rem;
}

/* Bandeau d'état : encadré bleuté du suivi, vert ou rouge selon l'issue. */
.meeting-banner {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  padding: 0.9rem 1.15rem;
  border: 1px solid #bfdbfe;
  border-radius: 0.65rem;
  background: #f8fbff;
  color: #111827;
  font-size: 0.875rem;
  line-height: 1.5;
}

.meeting-banner__main {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.6rem 0.9rem;
}

.meeting-banner__main p {
  flex: 1 1 20rem;
  margin: 0;
}

.meeting-banner--completed {
  border-color: #bbf7d0;
  background: #f0fdf4;
}

.meeting-banner--failed {
  border-color: #fecaca;
  background: #fef2f2;
}

.meeting-banner__error {
  margin: 0;
  padding: 0.6rem 0.8rem;
  border-radius: 0.5rem;
  background: #ffffff;
  color: #b91c1c;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.78rem;
  overflow-wrap: anywhere;
}

.meeting-steps {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1.5rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.meeting-step {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #9ca3af;
  font-size: 0.8rem;
}

.meeting-step__marker {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.35rem;
  height: 1.35rem;
  border: 1px solid #d1d5db;
  border-radius: 50%;
  background: #ffffff;
  font-size: 0.65rem;
}

.meeting-step--done {
  color: #166534;
}

.meeting-step--done .meeting-step__marker {
  border-color: #22c55e;
  background: #22c55e;
  color: #ffffff;
}

.meeting-step--current {
  color: #2563eb;
  font-weight: 600;
}

.meeting-step--current .meeting-step__marker {
  border-color: #2563eb;
  color: #2563eb;
}

/* Points d'attention : même bandeau ambré que les alertes du tableau de bord. */
.meeting-warnings {
  padding: 1rem 1.25rem;
  border: 1px solid rgba(245, 158, 11, 0.25);
  border-radius: 16px;
  background: linear-gradient(140deg, #fef3c7, #fff9ed);
  color: #92400e;
  font-size: 0.875rem;
  line-height: 1.5;
}

.meeting-warnings__title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0 0.4rem;
  color: #111827;
  font-weight: 600;
}

.meeting-warnings__title i {
  color: #f59e0b;
}

.meeting-warnings ul {
  margin: 0;
  padding-left: 1.1rem;
  list-style: disc;
}

.meeting-warnings li + li {
  margin-top: 0.25rem;
}

.meeting-tabs {
  align-self: flex-start;
}

.meeting-tab-body {
  min-height: 12rem;
  color: #111827;
  font-size: 14px;
  line-height: 1.6;
}

.meeting-section-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem 1rem;
  margin-bottom: 0.9rem;
}

.meeting-section-actions__hint {
  margin-right: auto;
  color: #6b7280;
  font-size: 0.8rem;
}

.meeting-section-intro {
  margin: 0 0 1rem;
  color: #4b5563;
  font-size: 0.8rem;
}

.meeting-placeholder {
  margin: 0;
  padding: 48px 1rem;
  color: #4b5563;
  text-align: center;
}

.meeting-transcript {
  margin: 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.meeting-speech {
  padding: 0.75rem 0;
  border-top: 1px solid #e5e7eb;
}

.meeting-speech:first-of-type {
  border-top: 0;
}

.meeting-speech__header {
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
  margin-bottom: 0.25rem;
}

.meeting-speech__time {
  color: #9ca3af;
  font-size: 0.75rem;
  font-variant-numeric: tabular-nums;
}

.meeting-speech__text {
  margin: 0;
  overflow-wrap: anywhere;
}

/* Correction du locuteur : lien discret révélé au survol de la prise de parole (toujours visible au doigt). */
.meeting-speech__fix {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-left: auto;
  padding: 0;
  border: 0;
  background: none;
  color: #2563eb;
  font: inherit;
  font-size: 0.78rem;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.meeting-speech:hover .meeting-speech__fix,
.meeting-speech__fix:focus-visible {
  opacity: 1;
}

.meeting-speech__fix:hover {
  text-decoration: underline;
}

.meeting-speech__fix:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
}

.meeting-speech__fix--doubt {
  opacity: 1;
}

.meeting-speech__doubt {
  color: #f59e0b;
  font-size: 0.75rem;
}

.meeting-speech--fixing {
  padding-bottom: 1rem;
}

.meeting-speech--mixed .meeting-speech__text {
  color: #6b7280;
  font-style: italic;
}

@media (hover: none) {
  .meeting-speech__fix {
    opacity: 1;
  }
}

.meeting-proposal {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.9rem 1.1rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.65rem;
  background: #ffffff;
}

.meeting-proposal + .meeting-proposal {
  margin-top: 0.75rem;
}

.meeting-proposal__body {
  min-width: 0;
}

.meeting-proposal__title {
  margin: 0 0 0.3rem;
  color: #111827;
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.35;
}

.meeting-proposal__brief {
  margin: 0;
  color: #4b5563;
}

/* Journal : même bandeau discret que la note « masqué » des préoccupations. */
.meeting-log {
  padding: 0.6rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  color: #6b7280;
  font-size: 0.8rem;
}

.meeting-log summary {
  cursor: pointer;
}

.meeting-log summary:hover {
  color: #111827;
}

.meeting-log ul {
  margin: 0.5rem 0 0.25rem;
  padding: 0;
  list-style: none;
}

.meeting-log__entry {
  display: flex;
  gap: 0.75rem;
  padding: 0.15rem 0;
}

.meeting-log__entry time {
  flex: 0 0 auto;
  color: #9ca3af;
  font-variant-numeric: tabular-nums;
}

.meeting-log__entry--warning {
  color: #b45309;
}

.meeting-log__entry--error {
  color: #b91c1c;
}

.md-content >>> h1,
.md-content >>> h2,
.md-content >>> h3,
.md-content >>> h4 {
  margin: 1.1rem 0 0.4rem;
  color: #111827;
  font-weight: 600;
  line-height: 1.3;
}

.md-content >>> h1 { font-size: 1.125rem; }
.md-content >>> h2 { font-size: 1.05rem; }
.md-content >>> h3 { font-size: 1rem; }
.md-content >>> h4 { font-size: 0.95rem; }
.md-content >>> h1:first-child,
.md-content >>> h2:first-child,
.md-content >>> h3:first-child { margin-top: 0; }
.md-content >>> p { margin: 0 0 0.7rem; }
.md-content >>> p:last-child { margin-bottom: 0; }
.md-content >>> strong { font-weight: 600; }
.md-content >>> ul { margin: 0.4rem 0 0.8rem; padding-left: 1.3rem; list-style: disc; }
.md-content >>> ol { margin: 0.4rem 0 0.8rem; padding-left: 1.3rem; list-style: decimal; }
.md-content >>> li { margin: 0.2rem 0; }
.md-content >>> a { color: #2563eb; text-decoration: underline; }
.md-content >>> blockquote { margin: 0.6rem 0; padding: 0.2rem 0.9rem; border-left: 3px solid #d1d5db; color: #4b5563; }
.md-content >>> code { padding: 0.1rem 0.3rem; border-radius: 4px; background: #f3f4f6; font-size: 0.85em; }
.md-content >>> table { margin: 0.6rem 0; border-collapse: collapse; font-size: 0.85rem; }
.md-content >>> th,
.md-content >>> td { padding: 0.35rem 0.6rem; border: 1px solid #e5e7eb; text-align: left; }
.md-content >>> th { background: #f9fafb; }
.md-content >>> hr { margin: 1rem 0; border: 0; border-top: 1px solid #e5e7eb; }

@media (max-width: 768px) {
  .meeting-body {
    padding: 1rem 1.25rem 1.25rem;
  }

  .meeting-proposal {
    flex-direction: column;
  }
}
</style>
